/** Typed route builders + React Router patterns (docs/PLAN.md §1.1–§1.3). */
const seg = (value: string) => encodeURIComponent(value);
const optional = (value: string | undefined) => (value ? `/${seg(value)}` : "");

export const CONSOLE_PATHS = {
  login: "/login",
  register: "/register",
  verifyEmail: "/verify-email",
  twoFactor: "/two-factor",
  forgotPassword: "/forgot-password",
  resetPassword: "/reset-password",
  acceptInvite: "/invite/:token",
  onboarding: "/onboarding",
  workspace: "/w/:ws",
  dashboard: "/w/:ws/dashboard",
  agents: "/w/:ws/agents",
  playground: "/w/:ws/agents/:agentId/playground",
  knowledge: "/w/:ws/agents/:agentId/knowledge",
  sourceDetail: "/w/:ws/knowledge/:sourceId",
  // Knowledge graph (M28, GR-G3-2): workspace-wide, the entity opens as a drawer addressed by its id.
  knowledgeGraph: "/w/:ws/knowledge-graph/:entityId?",
  agentSettings: "/w/:ws/agents/:agentId/settings/:tab?",
  actions: "/w/:ws/agents/:agentId/actions",
  evaluations: "/w/:ws/agents/:agentId/evaluations",
  widgetStudio: "/w/:ws/agents/:agentId/widget/:tab?",
  webappStudio: "/w/:ws/agents/:agentId/webapp/:tab?",
  channels: "/w/:ws/agents/:agentId/channels",
  inbox: "/w/:ws/inbox/:conversationId?",
  gaps: "/w/:ws/gaps",
  leads: "/w/:ws/leads/:leadId?",
  workflows: "/w/:ws/workflows",
  workflow: "/w/:ws/workflows/:workflowId/:tab?",
  announcements: "/w/:ws/announcements",
  // Supervisor wallboard (M29 RPT-003): live tiles and the agent board; behind live_chat_ops and wallboard.view.
  wallboard: "/w/:ws/wallboard",
  analytics: "/w/:ws/analytics",
  audiences: "/w/:ws/audiences/:tab?",
  team: "/w/:ws/team",
  roles: "/w/:ws/settings/roles/:roleId?",
  connectors: "/w/:ws/settings/connectors",
  connectorImport: "/w/:ws/settings/connectors/import",
  connector: "/w/:ws/settings/connectors/:connectorId/:tab?",
  connectorOperation: "/w/:ws/settings/connectors/:connectorId/operations/:operationKey",
  billing: "/w/:ws/billing",
  settings: "/w/:ws/settings/:panel?",
  guide: "/w/:ws/guide/:topicId?",
  tickets: "/w/:ws/tickets/:number?",
  organisations: "/w/:ws/organisations/:orgId?/:tab?",
  contacts: "/w/:ws/contacts/:contactId?",
  assets: "/w/:ws/assets/:assetId?",
  installations: "/w/:ws/installations/:projectId?",
  // Service contracts (C08): a tab of the Assets page, with its own address; the contract opens as a drawer.
  contracts: "/w/:ws/assets/contracts/:contractId?",
  // Spare-part requests (AST-092): another tab of the Assets page; the request opens as a drawer.
  spares: "/w/:ws/assets/spares/:requestId?",
  // Customer segments (M29 CRM-005): a page under Contacts with its own address; the static word wins over :contactId.
  segments: "/w/:ws/contacts/segments",
  visits: "/w/:ws/visits/:visitId?",
  // The engineer's phone screens (C23): My day, then one visit and its step (on site, checklist, measures, photos, sign, done).
  myDay: "/w/:ws/my-day/:visitId?/:step?",
  orders: "/w/:ws/orders/:orderId?",
  catalogue: "/w/:ws/catalogue",
  serviceReports: "/w/:ws/service-reports/:tab?",
  serviceSettings: "/w/:ws/service/settings/:tab?",
} as const;

export const consoleRoutes = {
  login: () => CONSOLE_PATHS.login,
  register: () => CONSOLE_PATHS.register,
  verifyEmail: () => CONSOLE_PATHS.verifyEmail,
  twoFactor: () => CONSOLE_PATHS.twoFactor,
  forgotPassword: () => CONSOLE_PATHS.forgotPassword,
  resetPassword: (token: string) => `${CONSOLE_PATHS.resetPassword}?token=${seg(token)}`,
  acceptInvite: (token: string) => `/invite/${seg(token)}`,
  onboarding: () => CONSOLE_PATHS.onboarding,
  dashboard: (ws: string) => `/w/${seg(ws)}/dashboard`,
  agents: (ws: string) => `/w/${seg(ws)}/agents`,
  playground: (ws: string, agentId: string) => `/w/${seg(ws)}/agents/${seg(agentId)}/playground`,
  knowledge: (ws: string, agentId: string) => `/w/${seg(ws)}/agents/${seg(agentId)}/knowledge`,
  sourceDetail: (ws: string, sourceId: string) => `/w/${seg(ws)}/knowledge/${seg(sourceId)}`,
  knowledgeGraph: (ws: string, entityId?: string) => `/w/${seg(ws)}/knowledge-graph${optional(entityId)}`,
  agentSettings: (ws: string, agentId: string, tab?: string) =>
    `/w/${seg(ws)}/agents/${seg(agentId)}/settings${optional(tab)}`,
  actions: (ws: string, agentId: string) => `/w/${seg(ws)}/agents/${seg(agentId)}/actions`,
  evaluations: (ws: string, agentId: string) => `/w/${seg(ws)}/agents/${seg(agentId)}/evaluations`,
  widgetStudio: (ws: string, agentId: string, tab?: string) =>
    `/w/${seg(ws)}/agents/${seg(agentId)}/widget${optional(tab)}`,
  webappStudio: (ws: string, agentId: string, tab?: string) =>
    `/w/${seg(ws)}/agents/${seg(agentId)}/webapp${optional(tab)}`,
  channels: (ws: string, agentId: string) => `/w/${seg(ws)}/agents/${seg(agentId)}/channels`,
  inbox: (ws: string, conversationId?: string) => `/w/${seg(ws)}/inbox${optional(conversationId)}`,
  gaps: (ws: string) => `/w/${seg(ws)}/gaps`,
  leads: (ws: string, leadId?: string) => `/w/${seg(ws)}/leads${optional(leadId)}`,
  workflows: (ws: string) => `/w/${seg(ws)}/workflows`,
  workflow: (ws: string, workflowId: string, tab?: string) =>
    `/w/${seg(ws)}/workflows/${seg(workflowId)}${optional(tab)}`,
  announcements: (ws: string) => `/w/${seg(ws)}/announcements`,
  wallboard: (ws: string) => `/w/${seg(ws)}/wallboard`,
  analytics: (ws: string) => `/w/${seg(ws)}/analytics`,
  audiences: (ws: string, tab?: string) => `/w/${seg(ws)}/audiences${optional(tab)}`,
  team: (ws: string) => `/w/${seg(ws)}/team`,
  roles: (ws: string, roleId?: string) => `/w/${seg(ws)}/settings/roles${optional(roleId)}`,
  connectors: (ws: string) => `/w/${seg(ws)}/settings/connectors`,
  connectorImport: (ws: string, source?: string) => `/w/${seg(ws)}/settings/connectors/import${source ? `?source=${seg(source)}` : ""}`,
  connector: (ws: string, connectorId: string, tab?: string) => `/w/${seg(ws)}/settings/connectors/${seg(connectorId)}${optional(tab)}`,
  connectorOperation: (ws: string, connectorId: string, operationKey: string) =>
    `/w/${seg(ws)}/settings/connectors/${seg(connectorId)}/operations/${seg(operationKey)}`,
  billing: (ws: string) => `/w/${seg(ws)}/billing`,
  settings: (ws: string, panel?: string) => `/w/${seg(ws)}/settings${optional(panel)}`,
  guide: (ws: string, topicId?: string) => `/w/${seg(ws)}/guide${optional(topicId)}`,
  tickets: (ws: string, number?: string) => `/w/${seg(ws)}/tickets${optional(number)}`,
  organisations: (ws: string, orgId?: string, tab?: string) =>
    `/w/${seg(ws)}/organisations${orgId ? `/${seg(orgId)}${optional(tab)}` : ""}`,
  contacts: (ws: string, contactId?: string) => `/w/${seg(ws)}/contacts${optional(contactId)}`,
  assets: (ws: string, assetId?: string) => `/w/${seg(ws)}/assets${optional(assetId)}`,
  installations: (ws: string, projectId?: string) => `/w/${seg(ws)}/installations${optional(projectId)}`,
  contracts: (ws: string, contractId?: string) => `/w/${seg(ws)}/assets/contracts${optional(contractId)}`,
  spares: (ws: string, requestId?: string) => `/w/${seg(ws)}/assets/spares${optional(requestId)}`,
  segments: (ws: string) => `/w/${seg(ws)}/contacts/segments`,
  visits: (ws: string, visitId?: string) => `/w/${seg(ws)}/visits${optional(visitId)}`,
  myDay: (ws: string, visitId?: string, step?: string) => `/w/${seg(ws)}/my-day${optional(visitId)}${optional(step)}`,
  orders: (ws: string, orderId?: string) => `/w/${seg(ws)}/orders${optional(orderId)}`,
  catalogue: (ws: string) => `/w/${seg(ws)}/catalogue`,
  serviceReports: (ws: string, tab?: string) => `/w/${seg(ws)}/service-reports${optional(tab)}`,
  serviceSettings: (ws: string, tab?: string) => `/w/${seg(ws)}/service/settings${optional(tab)}`,
} as const;

export const ADMIN_PATHS = {
  login: "/login",
  dashboard: "/dashboard",
  tenants: "/tenants",
  tenantDetail: "/tenants/:id/:tab?",
  usage: "/usage",
  plans: "/plans",
  salesRequests: "/sales-requests",
  aiModels: "/ai-models/:tab?",
  system: "/system",
  support: "/support/:ticketId?",
  audit: "/audit",
  staff: "/staff",
  settings: "/settings/:panel?",
} as const;

export const adminRoutes = {
  login: () => ADMIN_PATHS.login,
  dashboard: () => ADMIN_PATHS.dashboard,
  tenants: () => ADMIN_PATHS.tenants,
  tenantDetail: (id: string, tab?: string) => `/tenants/${seg(id)}${optional(tab)}`,
  usage: () => ADMIN_PATHS.usage,
  plans: () => ADMIN_PATHS.plans,
  salesRequests: () => ADMIN_PATHS.salesRequests,
  aiModels: (tab?: string) => `/ai-models${optional(tab)}`,
  system: () => ADMIN_PATHS.system,
  support: (ticketId?: string) => `/support${optional(ticketId)}`,
  audit: () => ADMIN_PATHS.audit,
  staff: () => ADMIN_PATHS.staff,
  settings: (panel?: string) => `/settings${optional(panel)}`,
} as const;

export const WEBAPP_PATHS = {
  login: "/login",
  home: "/",
  conversation: "/c/:conversationId",
  /** A read-only snapshot of a shared conversation: `/chat/<slug>/s/<token>`; the token is the whole address. */
  shared: "/s/:token",
} as const;

export const webappRoutes = {
  login: () => WEBAPP_PATHS.login,
  home: () => WEBAPP_PATHS.home,
  conversation: (conversationId: string) => `/c/${seg(conversationId)}`,
  shared: (token: string) => `/s/${seg(token)}`,
} as const;
